import styled from "styled-components";
import { Button, ButtonRow, Card } from "../../App.styles";
import theme from '../../themes'

const defaultWidth = 276
export const getItemWidth = containerWidth => {
    // make sure we use peek-a-boos on the carousel so that an edge of next object always visible;
    // use this format for the nub items: 30% * 3 + 10%
    const itemGap = 6
    const itemWidth = containerWidth
        ? .3 * containerWidth - itemGap
        : defaultWidth
    return `${Math.min(itemWidth, defaultWidth)}px`
}

export const UploadBody = styled('div', {
    shouldForwardProp: (props) => !['container'].includes(props),
})(({ theme, container }) => {
    const itemWidth = getItemWidth(container?.clientWidth)

    return `
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: ${theme.rounded.md};
        border: 2px dashed ${theme.primaryColor};
        background: ${theme.cardColor}50;
        width: ${defaultWidth}px;
        height: 325px;
        position: absolute;
        bottom: 0;
        left: 0;

        .svg-inline--fa:not([data-icon="question-circle"]) {
            width: 72px;
            height: 50px;
            color: ${theme.primaryColor};
        }

        .btn {
            border-radius: ${theme.rounded.xs};
            display: block;
            width: 100%;
            white-space: normal;

            span {
                text-transform: capitalize;
            }
        }

        ${theme.breakpoints.up('sm')} {
            width: ${itemWidth};
            min-width: ${itemWidth};
            height: auto;
            position: relative;
            bottom: auto;
            right: auto;

            .btn.btn--flat {
                padding: .625rem .5rem .5rem .5rem;
            }
        }
    `
});

export const NubsContainer = styled.div(({ theme }) => `
    width: 100%;
    max-width: 280px;
    overflow: auto;
    display: flex;

    ${theme.breakpoints.up('sm')} {
        max-width: none;
    }
`)

export const NubsBody = styled('div', {
    shouldForwardProp: (props) => !['container'].includes(props),
})(({ theme, container }) => {
    const itemWidth = getItemWidth(container?.clientWidth)
    return `
        display: flex;
        padding-bottom: calc(325px + 20px);
        flex-wrap: wrap;
        gap: 12px;

        .nubs__video-thumbnail {
            min-width: ${defaultWidth}px;
            border-radius: ${theme.rounded.md};
        }

        ${theme.breakpoints.up('sm')} {
            padding-bottom: 0;
            flex-wrap: nowrap;

            .nubs__video-thumbnail {
                min-width: ${itemWidth};
            }
        }
    `
})

export const NubsItem = styled('div', {
    shouldForwardProp: (props) => !['isImage', 'container'].includes(props),
})(({ theme, isImage, container }) => {
    const itemWidth = getItemWidth(container?.clientWidth)
    return `
        background: ${theme.white};
        border-radius: ${theme.rounded.md};
        border: 2px solid ${theme.primaryColor};
        padding: ${isImage ? '0' : '12px'};
        max-height: 400px;
        min-width: ${isImage ? 'unset' : `${defaultWidth}px`};
        overflow: ${isImage ? 'unset' : 'auto'};
        display: flex;

        > img {
            max-width: ${defaultWidth}px;
            object-fit: contain;
            border-radius: calc(${theme.rounded.md} - 2px);
        }

        > iframe {
            width: ${defaultWidth}px;
            height: auto;
            border-radius: calc(${theme.rounded.md} - 2px);
        }

        .nubs__image {
            min-width: ${defaultWidth}px;
            width: 100%;
            height: 100%;
            object-fit: contain;
        }

        .image-thumbnail-loader__preview {
            background: transparent;
            margin-right: 0;
            margin-bottom: 0;
            overflow: hidden;
            border-radius: calc(${theme.rounded.md} - 2px);
            max-height: 100%;
        }

        &.nubs__item--preview {
            padding: 12px;

            iframe {
                width: 100%;
            }
        }

        ${theme.breakpoints.up('sm')} {
            min-width: ${isImage ? 'unset' : `${itemWidth}`};

            > img {
                max-width: ${itemWidth};
            }

            > iframe {
                width: ${itemWidth};
            }

            .nubs__image {
                min-width: ${itemWidth};
                width: ${itemWidth};
            }
        }

        &:hover .nubs-action__buttons {
            top: 12px;
            opacity: 1;
        }
    `
})

export const NubsBlankPlaceholder = styled('div', {
    shouldForwardProp: (props) => !['container'].includes(props),
})(({ theme, container }) => {
    const itemWidth = getItemWidth(container?.clientWidth)

    return `
        background: #D6D6D6;
        border-radius: ${theme.rounded.md};
        min-width: ${defaultWidth}px;
        height: 325px;
        display: flex;

        ${theme.breakpoints.up('sm')} {
            min-width: ${itemWidth};
        }
    `
})

export const NubsLoader = styled.div(({ isLoading }) => `
    position: absolute;
    top: 0;
    left: 0;
    transform: none;
    width: 100%;
    height: 100%;
    background: rgba(255,255,255,.75);
    display: ${isLoading ? 'flex' : 'none'};
    flex-direction: column;
    align-items: center;
    justify-content: center;
`)

export const NubsFragment = styled.div(() => `
    .modal--html {
        .modal-body {
            padding: 0;
            
            .form-outline {
                margin-bottom: 0;
            }
    
            .fr-box.fr-basic .fr-wrapper,
            .fr-toolbar.fr-top,
            .fr-second-toolbar {
                border: none;
            }
        }
    }
`)

export const NubsActionButtons = styled.div(({ theme }) => `
    position: absolute;
    opacity: 0;
    top: -24px;
    right: 12px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 16px;
    background: ${theme.primaryColor};
    border-radius: 6px;
    padding: 0 12px;
    transition: opacity, top .25s;
`)

export const NubsActionButton = styled(Button)(({ theme }) => `
    padding: 0;
    font-size: 24px;
    
    &.btn-link {
        color: ${theme.white};

        &:hover {
            background: transparent;
        }
    }

    svg {
        font-size: 20px;
    }
`)

export const NubsDragIcon = styled.span(({ theme }) => `
    position: absolute;
    bottom: 12px;
    right: 12px;
    color: ${theme.darkGreyColor};
    font-size: 18px;
    background: ${theme.lightColor};
    padding: 2px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
`)

export const DroppableList = styled.div(() => `
    display: flex;
    gap: 12px;

    &:empty {
        margin-right: -12px;
    }
`)

export const DraggableItem = styled.div(() => `
    user-select: none;
    display: flex;
    position: relative;

    &:hover .nubs-action__buttons {
        top: 12px;
        opacity: 1;
    }
`)

export const NubsText = styled('p', {
    shouldForwardProp: (props) => !['container'].includes(props),
})(({ container }) => {
    const itemWidth = getItemWidth(container?.clientWidth)
    return `
        max-width: calc(${defaultWidth}px - 24px);

        ${theme.breakpoints.up('sm')} {
            max-width: calc(${itemWidth} - 24px);
        }
    `
})

export const ImageUploadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  .form-label {
    font-size: 0.8rem;
  }

  ${ButtonRow} {
    margin-top: 0.75rem;
    margin-bottom: 0.25rem;

    ${Button} {
      margin-right: 0.25rem;
      max-width: 200px;
      text-overflow: ellipsis;
    }
  }
`;

export const FileWrapper = styled.div`
  margin-right: 0.5rem;

  input[type="file"] {
    display: none;
  }

  .form-control {
    font-size: 0.8rem;
  }
`;

export const ActionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;

  button,
  .btn {
    margin-bottom: 0;
    padding: .625rem 1rem;
  }
`;

export const UploadBtn = styled.label(({ theme }) => `
    display: inline-block;
    border-radius: ${theme.rounded.sm};
    width: 100%;

    &:focus {
        outline: 2px solid ${theme.accent2Color}
    }

    .btn.btn-primary {
        margin-bottom: 0;
    }
`);

export const VideoWrapper = styled.div(({ theme }) => `
  position: relative;
  background: #000;
  border-radius: ${theme.rounded.sm};
  overflow: hidden;

  > svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
    color: ${theme.darkColor};
  }
`)

export const DescriptionHelp = styled.div(() => `
  width: 100%;

  .row-info__container {
    .col-12 .btn {
        margin-right: 0;
    }

    .col-2 .btn {
        display: flex;
        padding: 4px;
        height: auto;
        color: ${theme.white};
    }
  }
`)

export const UploadProgressContent = styled(Card)(() => `
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 12px;
  padding: 12px;
`)

export const NubsPlaceholderRow = styled.div(() => `
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: center;
`)

export const NubsPlaceholderCol = styled.div(() => `
    &:nth-of-type(3n+1) {
        width: 50%;
    }
`)
